import React, { useState, useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import { CircularProgress, TextField, Button, Grid } from "@mui/material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "utils/api";
import { toast } from "react-toastify";
import DiscountIcon from "@mui/icons-material/Discount";

function RegistrationFeesManagement() {
  const [registrationFees, setRegistrationFees] = useState(""); // Change to string for TextField
  const [discount, setDiscount] = useState(""); // Change to string for TextField
  const [finalAmount, setFinalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Calculate final amount on client side for better user experience
  const calculatedFinalAmount = useMemo(() => {
    const fee = Number(registrationFees) || 0;
    const disc = Number(discount) || 0;

    if (disc < 0 || disc > 100 || fee < 0) return 0;

    const discountAmount = fee * (disc / 100);
    const final = fee - discountAmount;
    return final >= 0 ? final : 0;
  }, [registrationFees, discount]);

  useEffect(() => {
    setFinalAmount(calculatedFinalAmount);
  }, [calculatedFinalAmount]);

  // Fetch registration fees amount
  const fetchRegistrationFees = async () => {
    setFetchLoading(true);
    try {
      const response = await api.get("/planwithstudentreview");
      if (response.data.success) {
        // Set state with fetched values (or 0 as default, converted to string for inputs)
        setRegistrationFees((response.data.data.registrationFees || 0).toString());
        setDiscount((response.data.data.registrationFeesDiscount || 0).toString());
        setFinalAmount(response.data.data.registrationFeesFinalAmount || 0);
      }
    } catch (error) {
      console.error("Error fetching registration fees:", error);
      toast.error(error.response?.data?.message || "Failed to fetch registration fees amount");
      setRegistrationFees("0");
      setDiscount("0");
      setFinalAmount(0);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrationFees();
  }, []);

  // Handle update
  const handleUpdate = async () => {
    // Basic validation
    const fee = Number(registrationFees);
    const disc = Number(discount);
    if (isNaN(fee) || isNaN(disc) || fee < 0 || disc < 0 || disc > 100) {
      toast.error("Please enter valid positive numbers for fees and a discount between 0 and 100.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.put("/planwithstudentreview/registration-fees", {
        registrationFees: fee,
        discount: disc,
      });
      if (response.data.success) {
        toast.success("Registration fees updated successfully!");
        setFinalAmount(response.data.data.finalAmount || 0); // Update final amount from backend response
        // Re-fetch to ensure consistency if the backend logic is complex, but in this case,
        // using the response data is sufficient.
      }
    } catch (error) {
      console.error("Error updating registration fees:", error);
      toast.error(error.response?.data?.message || "Failed to update registration fees");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox
                display="flex"
                alignItems="center"
                mb={3}
                borderBottom="1px solid #ccc"
                pb={2}
              >
                <CurrencyRupeeIcon color="primary" sx={{ mr: 1, fontSize: 30 }} />
                <ArgonTypography variant="h5" fontWeight="medium">
                  Registration Fees Management
                </ArgonTypography>
              </ArgonBox>

              {fetchLoading ? (
                <ArgonBox textAlign="center" py={4}>
                  <CircularProgress color="primary" />
                  <ArgonTypography variant="body2" color="text" mt={2}>
                    Loading registration fees amount...
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <ArgonBox>
                  <Grid container spacing={3}>
                    {/* Registration Fees Input */}
                    <Grid item xs={12} sm={6}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="caption" fontWeight="bold" gutterBottom>
                          Registration Fees Amount (₹)
                        </ArgonTypography>
                        <TextField
                          fullWidth
                          type="number"
                          placeholder="e.g. 5000"
                          value={registrationFees}
                          onChange={(e) => setRegistrationFees(e.target.value)}
                          disabled={loading}
                          size="large" // Increase size for better touch/click target
                          inputProps={{ min: 0 }}
                          sx={{ "& input": { padding: "10px 14px" } }}
                        />
                      </ArgonBox>
                    </Grid>

                    {/* Discount Input */}
                    <Grid item xs={12} sm={6}>
                      <ArgonBox mb={2}>
                        <ArgonTypography variant="caption" fontWeight="bold" gutterBottom>
                          Discount (%)
                        </ArgonTypography>
                        <TextField
                          fullWidth
                          type="number"
                          placeholder="e.g. 10"
                          value={discount}
                          onChange={(e) => setDiscount(e.target.value)}
                          disabled={loading}
                          size="large" // Increase size for better touch/click target
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ "& input": { padding: "10px 14px" } }}
                        />
                      </ArgonBox>
                    </Grid>
                  </Grid>

                  {/* Calculated Amount Section */}
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={3}
                    p={2}
                    borderRadius="8px"
                    bgcolor="lightgray" // Light background for emphasis
                  >
                    <ArgonTypography variant="body1" fontWeight="bold" color="dark">
                      Calculated Final Amount:
                    </ArgonTypography>
                    <ArgonTypography variant="h5" fontWeight="bold" color="primary">
                      ₹{calculatedFinalAmount.toFixed(2)}
                    </ArgonTypography>
                  </ArgonBox>

                  {/* Update Button */}
                  <ArgonBox display="flex" justifyContent="flex-end" mt={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdate}
                      disabled={loading}
                      sx={{ color: "#FFFFFF", padding: "10px 25px" }}
                    >
                      {loading ? (
                        <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
                      ) : (
                        "Update Fees Structure"
                      )}
                    </Button>
                  </ArgonBox>

                  {/* Current Status/Last Saved Values */}
                  <ArgonBox mt={4} pt={2} borderTop="1px dashed #ddd">
                    <ArgonTypography variant="subtitle2" fontWeight="medium" mb={1}>
                      Current Active Settings (Last Saved):
                    </ArgonTypography>
                    <ArgonBox display="flex" gap={4}>
                      <ArgonBox>
                        <ArgonTypography variant="body2" color="text">
                          <CurrencyRupeeIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", mr: 0.5 }}
                          />
                          Registration Fees:{" "}
                          <ArgonTypography component="span" fontWeight="bold">
                            ₹{Number(registrationFees).toFixed(2)}
                          </ArgonTypography>
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox>
                        <ArgonTypography variant="body2" color="text">
                          <DiscountIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", mr: 0.5 }}
                          />
                          Discount:{" "}
                          <ArgonTypography component="span" fontWeight="bold">
                            {Number(discount).toFixed(2)}%
                          </ArgonTypography>
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonBox>
                        <ArgonTypography variant="body2" color="text" fontWeight="bold">
                          <CurrencyRupeeIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", mr: 0.5 }}
                          />
                          Final Amount:{" "}
                          <ArgonTypography
                            component="span"
                            variant="body1"
                            color="success"
                            fontWeight="bold"
                          >
                            ₹{finalAmount.toFixed(2)}
                          </ArgonTypography>
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </ArgonBox>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RegistrationFeesManagement;
